<template>
    <div class="border border-gray-200 rounded-lg overflow-hidden bg-white shadow-sm" data-testid="summary-wizard-preview">
        <div class="h-2 bg-slate-700"></div>

        <div class="p-4">
            <h4 class="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1">Live Preview</h4>
            <p class="font-semibold text-gray-800 mb-3 truncate">
                {{ answers.name || 'Untitled Summary Sheet' }}
            </p>

            <div v-if="calculatedColumns.length === 0"
                class="text-center py-10 border-2 border-dashed border-gray-200 rounded-lg text-gray-400 text-sm">
                <i class="pi pi-list text-2xl mb-2 block text-gray-300"></i>
                Your summary sheet builds up here as you answer.
            </div>

            <div v-else class="overflow-x-auto">
                <table class="w-full text-[11px] border border-gray-200">
                    <thead>
                        <!-- Group row: Previous / Current / Cumulative blocks -->
                        <tr class="bg-gray-100 text-gray-700">
                            <th v-for="col in infoColumns" :key="col.key" rowspan="2"
                                class="px-2 py-1 text-left font-semibold border border-gray-200 whitespace-nowrap align-bottom">
                                {{ col.label }}
                            </th>
                            <template v-for="group in columnGroups" :key="group.name || group.columns[0]?.key">
                                <th v-if="group.name" :colspan="group.columns.length"
                                    class="px-2 py-1 text-center font-semibold border border-gray-200 bg-gray-200/60">
                                    {{ group.name }}
                                </th>
                                <template v-else>
                                    <th v-for="col in group.columns" :key="col.key" rowspan="2"
                                        class="px-2 py-1 text-center font-semibold border border-gray-200 whitespace-nowrap align-bottom">
                                        {{ col.label }}
                                    </th>
                                </template>
                            </template>
                        </tr>
                        <!-- Sub-columns inside each grouped block -->
                        <tr class="bg-gray-50 text-gray-500">
                            <template v-for="group in columnGroups" :key="`sub-${group.name || 'flat'}-${group.columns[0]?.key}`">
                                <template v-if="group.name">
                                    <th v-for="col in group.columns" :key="col.key"
                                        class="px-2 py-0.5 text-center font-medium border border-gray-200 whitespace-nowrap">
                                        {{ col.label }}
                                    </th>
                                </template>
                            </template>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="row in rows" :key="String(row.info.matric_no)" class="odd:bg-white even:bg-gray-50">
                            <td v-for="col in infoColumns" :key="col.key"
                                class="px-2 py-1 text-gray-700 border border-gray-100 whitespace-nowrap">
                                {{ row.info[col.key] }}
                            </td>
                            <td v-for="col in calculatedColumns" :key="col.key"
                                class="px-2 py-1 text-center text-gray-600 border border-gray-100 whitespace-nowrap">
                                {{ row.values[col.key] }}
                            </td>
                        </tr>
                    </tbody>
                </table>

                <!-- Extras chips -->
                <div v-if="answers.extras.length > 0" class="flex flex-wrap gap-2 mt-3">
                    <span v-for="extra in selectedExtraOptions" :key="extra.key"
                        class="bg-emerald-50 text-emerald-700 border border-emerald-100 px-2 py-1 rounded text-[11px]"
                        v-tooltip.top="extra.description">
                        + {{ extra.label }}
                    </span>
                </div>

                <p class="text-[10px] text-gray-400 mt-2">
                    Sample data — the real sheet lists every student with values computed from their
                    course registrations and scores.
                </p>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import {
  EXTRA_OPTIONS,
  buildSummaryColumnDrafts,
  type SummaryColumnDraft,
  type SummaryPreviewRow,
  type SummaryWizardAnswers,
} from '@/utils/summaryWizard';

const props = defineProps<{
    answers: SummaryWizardAnswers;
    rows: SummaryPreviewRow[];
}>();

const allColumns = computed(() => buildSummaryColumnDrafts(props.answers));
const infoColumns = computed(() => allColumns.value.filter((c) => c.type === 'STUDENT_INFO'));
const calculatedColumns = computed(() => allColumns.value.filter((c) => c.type !== 'STUDENT_INFO'));

/** Consecutive columns sharing a group render under one spanning header. */
const columnGroups = computed(() => {
  const groups: Array<{ name: string | null; columns: SummaryColumnDraft[] }> = [];
  calculatedColumns.value.forEach((col) => {
    const last = groups[groups.length - 1];
    if (last && last.name === col.group) last.columns.push(col);
    else groups.push({ name: col.group, columns: [col] });
  });
  return groups;
});

const selectedExtraOptions = computed(
  () => EXTRA_OPTIONS.filter((e) => props.answers.extras.includes(e.key)),
);
</script>
